import { Injectable, signal, computed, inject } from '@angular/core';
import { Branding, BrandingColors, BrandingFont } from '../interfaces/api-interfaces';
import { ApiService } from './api.service';
import { ThemeService } from './theme.service';
import { firstValueFrom } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class BrandingService {
    private readonly apiService = inject(ApiService);
    private readonly themeService = inject(ThemeService);

    private brandingSignal = signal<Branding | null>(null);
    private loadingSignal = signal<boolean>(false);
    private errorSignal = signal<string | null>(null);

    constructor() {
        // Listen for theme changes and reapply branding
        this.themeService.themeChanged.subscribe(() => {
            this.reapplyBranding();
        });
    }

    // Public computed signals
    public branding = computed(() => this.brandingSignal());
    public loading = computed(() => this.loadingSignal());
    public error = computed(() => this.errorSignal());

    // Convenience computed signals for easy access
    public platformName = computed(() => this.brandingSignal()?.platform.name ?? 'FreyMuT E-Learning Platform');
    public platformShortName = computed(() => this.brandingSignal()?.platform.shortName ?? 'FreyMuT');
    public platformTagline = computed(() => this.brandingSignal()?.platform.tagline ?? '');
    public colors = computed(() => this.brandingSignal()?.colors);
    public gradient = computed(() => this.brandingSignal()?.gradient);

    /**
     * Main logo URL for header and login pages
     * @returns {string | null} Logo URL or null if not available or invalid
     */
    public logo = computed(() => {
        const logoUrl = this.brandingSignal()?.logos?.logo ?? null;
        return this.isValidImageUrl(logoUrl) ? this.toAbsoluteUrl(logoUrl) : null;
    });

    /**
     * Small logo URL for mobile/compact views
     * @returns {string | null} Small logo URL or null if not available or invalid
     */
    public logoSmall = computed(() => {
        const logoUrl = this.brandingSignal()?.logos?.logoSmall ?? null;
        return this.isValidImageUrl(logoUrl) ? this.toAbsoluteUrl(logoUrl) : null;
    });

    /**
     * Favicon URL for browser tab icon
     * @returns {string | null} Favicon URL or null if not available or invalid
     */
    public favicon = computed(() => {
        const faviconUrl = this.brandingSignal()?.logos?.favicon ?? null;
        return this.isValidImageUrl(faviconUrl) ? this.toAbsoluteUrl(faviconUrl) : null;
    });

    /**
     * Helper computed signal to check if main logo is available
     * @returns {boolean} True if logo URL is valid and available
     */
    public hasLogo = computed(() => !!this.logo());

    /**
     * Helper computed signal to check if small logo is available
     * @returns {boolean} True if small logo URL is valid and available
     */
    public hasLogoSmall = computed(() => !!this.logoSmall());

    /**
     * Helper computed signal to check if favicon is available
     * @returns {boolean} True if favicon URL is valid and available
     */
    public hasFavicon = computed(() => !!this.favicon());

    /**
     * Fetches branding information from the backend
     */
    async fetchBranding(): Promise<void> {
        this.loadingSignal.set(true);
        this.errorSignal.set(null);

        try {
            const branding = await firstValueFrom(this.apiService.getBranding());
            this.brandingSignal.set(branding);

            // Apply branding to document
            this.applyBrandingToDocument(branding);
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Failed to fetch branding';
            this.errorSignal.set(errorMessage);
            console.error('Failed to fetch branding:', error);

            // Set default branding on error
            this.setDefaultBranding();
        } finally {
            this.loadingSignal.set(false);
        }
    }

    /**
     * Sets default branding values
     */
    private setDefaultBranding(): void {
        const defaultBranding: Branding = {
            platform: {
                name: 'FreyMuT E-Learning Platform',
                shortName: 'FreyMuT',
                tagline: '',
            },
            colors: {
                sageGreen: '#82AC9F',
                mediumBlue: '#50a4c2',
                lightBlue: '#4da1d1',
                berryAccent: '#AB1D79',
                primary: '#82AC9F',
                secondary: '#50a4c2',
                accent: '#AB1D79',
                white: '#ffffff',
                black: '#000000',
                grayLight: '#f3f4f6',
                grayDark: '#374151',
                success: '#10b981',
                warning: '#f59e0b',
                error: '#ef4444',
                info: '#3b82f6',
            },
            gradient: {
                name: ['F', 'Y', 'T'],
                colors: ['#82AC9F', '#50a4c2', '#4da1d1'],
            },
            logos: undefined,
            fonts: {
                default: {
                    family: 'Roboto Slab',
                    weight: 400,
                    style: 'normal',
                    googleFontsUrl: 'https://fonts.googleapis.com/css2?family=Roboto+Slab:wght@400;700&display=swap'
                },
                heading: {
                    family: 'Roboto Slab',
                    weight: 700,
                    style: 'normal'
                }
            }
        };

        this.brandingSignal.set(defaultBranding);
        this.applyBrandingToDocument(defaultBranding);
    }

    /**
     * Applies branding to the document (title, meta tags, CSS variables)
     */
    private applyBrandingToDocument(branding: Branding): void {
        // Update document title
        document.title = branding.platform.name;

        // Get current theme mode
        const isDarkMode = this.themeService?.isDarkTheme() ?? false;

        // Apply DaisyUI dynamic theme
        this.applyDynamicTheme(branding, isDarkMode);

        // Apply dynamic fonts
        this.applyDynamicFonts(branding);

        // Update CSS custom properties for brand colors
        if (branding.colors) {
            const root = document.documentElement;
            root.style.setProperty('--brand-primary', branding.colors.primary);
            root.style.setProperty('--brand-secondary', branding.colors.secondary);
            root.style.setProperty('--brand-accent', branding.colors.accent);
            root.style.setProperty('--brand-sage-green', branding.colors.sageGreen);
            root.style.setProperty('--brand-medium-blue', branding.colors.mediumBlue);
            root.style.setProperty('--brand-light-blue', branding.colors.lightBlue);
            root.style.setProperty('--brand-berry-accent', branding.colors.berryAccent);
        }

        // Update meta tags
        this.updateMetaTags(branding);

        // Update favicon if provided
        this.updateFavicon(branding);
    }

    /**
     * Updates meta tags with branding information
     */
    private updateMetaTags(branding: Branding): void {
        const updateMetaTag = (name: string, content: string) => {
            let meta = document.querySelector(`meta[name="${name}"]`);
            if (!meta) {
                meta = document.createElement('meta');
                meta.setAttribute('name', name);
                document.head.appendChild(meta);
            }
            meta.setAttribute('content', content);
        };

        updateMetaTag('application-name', branding.platform.name);
        if (branding.platform.tagline) {
            updateMetaTag('description', branding.platform.tagline);
        }
        updateMetaTag('theme-color', branding.colors.primary);
    }

    /**
     * Validates that an image URL is safe to use
     * @param url The URL to validate
     * @returns {boolean} True if the URL is valid and uses a safe protocol
     */
    private isValidImageUrl(url: string | null | undefined): boolean {
        if (!url) return false;

        // Allow relative URLs (starting with /)
        if (url.startsWith('/')) return true;

        try {
            const urlObj = new URL(url);
            // Only allow http(s) and data protocols for images
            return ['http:', 'https:', 'data:'].includes(urlObj.protocol);
        } catch {
            // Invalid URL format
            return false;
        }
    }

    /**
     * Converts relative URLs to absolute URLs using the API base URL
     * @param url The URL to convert (can be relative or absolute)
     * @returns {string} Absolute URL
     */
    private toAbsoluteUrl(url: string | null): string | null {
        if (!url) return null;

        // If already absolute, return as-is
        if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) {
            return url;
        }

        // Convert relative URL to absolute using API base URL
        return this.apiService.baseUrl + url;
    }

    /**
     * Updates favicon dynamically based on branding
     */
    private updateFavicon(branding: Branding): void {
        const faviconUrl = branding.logos?.favicon;
        if (!faviconUrl) {
            // No custom favicon - keep default
            return;
        }

        // Find existing favicon link element
        let faviconLink = document.querySelector<HTMLLinkElement>('link[rel="icon"]');

        if (!faviconLink) {
            // Create new favicon link if it doesn't exist
            faviconLink = document.createElement('link');
            faviconLink.rel = 'icon';
            faviconLink.type = 'image/x-icon';
            document.head.appendChild(faviconLink);
        }

        // Update favicon href
        faviconLink.href = faviconUrl;
    }

    /**
     * Apply dynamic DaisyUI theme colors based on branding
     */
    private applyDynamicTheme(branding: Branding, isDarkMode: boolean): void {
        const root = document.documentElement;
        const colors = branding.colors;

        // Check if backend provides explicit dark variants (future enhancement)
        const darkColors = colors.dark ?? null;
        const themeColors = isDarkMode && darkColors
            ? darkColors
            : (isDarkMode ? this.generateDarkVariant(colors) : colors);

        // Apply DaisyUI theme variables
        root.style.setProperty('--color-primary', themeColors.primary);
        root.style.setProperty('--color-secondary', themeColors.secondary);
        root.style.setProperty('--color-accent', themeColors.accent);
        root.style.setProperty('--color-success', themeColors.success);
        root.style.setProperty('--color-warning', themeColors.warning);
        root.style.setProperty('--color-error', themeColors.error);
        root.style.setProperty('--color-info', themeColors.info);

        // Apply base colors (background/text) based on theme
        this.applyBaseColors(isDarkMode);
    }

    /**
     * Darkening percentages for generating dark mode variants
     * Adjusted per color for optimal visibility and contrast
     */
    private readonly DARK_VARIANT_PERCENTAGES = {
        primary: 12,    // Subtle darkening for main brand color
        secondary: 15,  // Standard darkening
        accent: 18,     // More pronounced for contrast
        success: 20,    // Darker green more visible on dark backgrounds
        warning: 15,    // Standard darkening
        error: 10,      // Preserve red vibrancy (less darkening)
        info: 15        // Standard darkening
    } as const;

    /**
     * Generate dark variants of colors using algorithmic darkening
     */
    private generateDarkVariant(colors: BrandingColors): {
        primary: string;
        secondary: string;
        accent: string;
        success: string;
        warning: string;
        error: string;
        info: string;
    } {
        /**
         * Darken a hex color by a percentage
         * @param hex - Hex color string (e.g., "#82AC9F")
         * @param percent - Percentage to darken (0-100)
         * @returns Darkened hex color
         */
        const darken = (hex: string, percent: number = 15): string => {
            // Validate hex format
            if (!/^#[0-9A-Fa-f]{6}$/.test(hex)) {
                console.warn(`Invalid hex color: ${hex}, returning original`);
                return hex;
            }

            const num = parseInt(hex.replace('#', ''), 16);
            const r = Math.max(0, Math.floor(((num >> 16) * (100 - percent)) / 100));
            const g = Math.max(0, Math.floor((((num >> 8) & 0x00FF) * (100 - percent)) / 100));
            const b = Math.max(0, Math.floor(((num & 0x0000FF) * (100 - percent)) / 100));

            // Ensure proper zero-padding for each color component
            return '#' + [r, g, b]
                .map(c => c.toString(16).padStart(2, '0'))
                .join('');
        };

        return {
            primary: darken(colors.primary, this.DARK_VARIANT_PERCENTAGES.primary),
            secondary: darken(colors.secondary, this.DARK_VARIANT_PERCENTAGES.secondary),
            accent: darken(colors.accent, this.DARK_VARIANT_PERCENTAGES.accent),
            success: darken(colors.success, this.DARK_VARIANT_PERCENTAGES.success),
            warning: darken(colors.warning, this.DARK_VARIANT_PERCENTAGES.warning),
            error: darken(colors.error, this.DARK_VARIANT_PERCENTAGES.error),
            info: darken(colors.info, this.DARK_VARIANT_PERCENTAGES.info),
        };
    }

    /**
     * Apply base colors (backgrounds and text) for light or dark mode
     */
    private applyBaseColors(isDarkMode: boolean): void {
        const root = document.documentElement;

        if (isDarkMode) {
            root.style.setProperty('--color-base-100', 'oklch(25.33% 0.016 252.42)');
            root.style.setProperty('--color-base-200', 'oklch(23.26% 0.014 253.1)');
            root.style.setProperty('--color-base-300', 'oklch(21.15% 0.012 254.09)');
            root.style.setProperty('--color-base-content', 'oklch(97.807% 0.029 256.847)');
        } else {
            root.style.setProperty('--color-base-100', 'oklch(98% 0.002 247.839)');
            root.style.setProperty('--color-base-200', 'oklch(96% 0.003 264.542)');
            root.style.setProperty('--color-base-300', 'oklch(92% 0.006 264.531)');
            root.style.setProperty('--color-base-content', 'oklch(21% 0.034 264.665)');
        }
    }

    /**
     * Apply dynamic fonts from branding
     */
    private applyDynamicFonts(branding: Branding): void {
        if (!branding.fonts) {
            return;
        }

        // Load Google Fonts if URL provided
        if (branding.fonts.default.googleFontsUrl) {
            this.loadGoogleFont(branding.fonts.default.googleFontsUrl);
        }

        // Apply default font to body
        this.applyFont('body', branding.fonts.default);

        // Apply heading font if specified (otherwise inherits default)
        if (branding.fonts.heading) {
            this.applyFont('h1, h2, h3, h4, h5, h6', branding.fonts.heading);
        }

        // Apply signature font if specified
        if (branding.fonts.signature) {
            this.applyFont('.signature, .quote-author', branding.fonts.signature);
        }
    }

    /**
     * Apply font to CSS selector
     */
    private applyFont(selector: string, font: BrandingFont): void {
        const root = document.documentElement;

        // Create CSS custom properties for this font
        const prefix = selector === 'body' ? '--font-default' : `--font-${selector}`;

        root.style.setProperty(`${prefix}-family`, font.family);
        root.style.setProperty(`${prefix}-weight`, font.weight.toString());
        root.style.setProperty(`${prefix}-style`, font.style);

        // Apply directly to elements via style injection
        const style = document.createElement('style');
        style.id = `branding-font-${selector.replace(/[^a-z]/gi, '-')}`;
        style.textContent = `
            ${selector} {
                font-family: "${font.family}", serif !important;
                font-weight: ${font.weight};
                font-style: ${font.style};
            }
        `;

        // Remove old style if exists
        const oldStyle = document.getElementById(style.id);
        if (oldStyle) {
            oldStyle.remove();
        }

        document.head.appendChild(style);
    }

    /**
     * Load Google Fonts dynamically
     */
    private loadGoogleFont(url: string): void {
        // Check if already loaded
        const existingLink = document.querySelector(`link[href="${url}"]`);
        if (existingLink) {
            return; // Already loaded
        }

        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = url;
        link.id = 'branding-google-fonts';

        // Remove old Google Fonts link if exists
        const oldLink = document.getElementById('branding-google-fonts');
        if (oldLink) {
            oldLink.remove();
        }

        document.head.appendChild(link);
    }

    /**
     * Public method to reapply branding (called by ThemeService on theme change)
     */
    public reapplyBranding(): void {
        const branding = this.brandingSignal();
        if (branding) {
            this.applyBrandingToDocument(branding);
        }
    }

    /**
     * Generates a gradient CSS string from the branding gradient
     */
    getGradientCSS(): string {
        const gradient = this.gradient();
        if (!gradient || gradient.colors.length === 0) {
            return 'linear-gradient(135deg, #82AC9F, #50a4c2, #4da1d1)';
        }

        const colorStops = gradient.colors.join(', ');
        return `linear-gradient(135deg, ${colorStops})`;
    }

    /**
     * Returns the gradient name as a string
     */
    getGradientName(): string {
        const gradient = this.gradient();
        if (!gradient || gradient.name.length === 0) {
            return 'FYT';
        }
        return gradient.name.join('');
    }
}